// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Site items drawn at the size they actually are.
 *
 * A logistics plan is an argument about whether things fit. Two trailers, a
 * dumpster and a lay-down pile all drawn as the same little badge cannot make
 * that argument — the drawing says nothing about whether the trailer clears
 * the fire lane, which is the question being asked.
 *
 * So an item carries a footprint in feet, and the drawing converts it through
 * the plan's scale. Feet rather than pixels because that is what the person
 * knows: a 10'x45' field office, a 20-yard roll-off. Change the scale and
 * every item stays the size it really is.
 *
 * Items with no footprint set fall back to a sensible default for their type,
 * so a plan drawn before any of this opens with trailers already trailer-sized
 * rather than with everything collapsed to a point.
 */

import type { SiteItemType, SiteIcon } from "./logistics";

export interface FootprintFt { w: number; h: number }

/** Smallest footprint worth drawing: below this it is a dot with a grab box. */
export const MIN_FT = 1;
/** A site item larger than this is an area, and the area tool draws those. */
export const MAX_FT = 1000;

/**
 * What each item is, in feet, when nobody has said otherwise.
 *
 * These are real sizes of real equipment, not guesses scaled for looks. Where
 * a type covers a range the common rental size is used.
 */
export const DEFAULT_FOOTPRINT_FT: Record<SiteItemType, FootprintFt> = {
  "field-trailer":    { w: 10, h: 40 },
  "office-trailer":   { w: 12, h: 60 },
  "temp-office":      { w: 10, h: 40 },
  "dumpster":         { w: 8,  h: 22 },   // 20-yard roll-off
  "porta-potty":      { w: 4,  h: 4 },
  "first-aid":        { w: 4,  h: 4 },
  "parking":          { w: 9,  h: 18 },   // one stall
  "generator":        { w: 4,  h: 10 },
  "crane-tower":      { w: 20, h: 20 },   // base pad
  "crane-mobile":     { w: 14, h: 50 },   // carrier; outriggers reach wider
  "material-lift":    { w: 8,  h: 8 },
  "dewatering":       { w: 6,  h: 10 },
  "fuel-tank":        { w: 8,  h: 12 },
  "staging-area":     { w: 40, h: 40 },
  "soil-stockpile":   { w: 30, h: 30 },
  "concrete-washout": { w: 8,  h: 12 },
  "gas-meter":        { w: 3,  h: 3 },
  "water-service":    { w: 3,  h: 3 },
  "electric-meter":   { w: 3,  h: 3 },
  "temp-power":       { w: 4,  h: 4 },
  "fire-hydrant":     { w: 2,  h: 2 },
  "sewer-connect":    { w: 3,  h: 3 },
};

export interface SizePreset { label: string; w: number; h: number }

/**
 * The sizes people actually order, per item type.
 *
 * Roll-offs from 20 up share one footprint and differ only in how tall the
 * walls are — a 30 and a 40 take the same ground. That looks like a mistake in
 * a list until you have stood next to them, so the labels carry the yardage
 * and the plan gets the truth.
 */
export const SIZE_PRESETS: Partial<Record<SiteItemType, SizePreset[]>> = {
  "dumpster": [
    { label: "5 yd",  w: 6,  h: 8 },
    { label: "10 yd", w: 8,  h: 12 },
    { label: "15 yd", w: 8,  h: 16 },
    { label: "20 yd", w: 8,  h: 22 },
    { label: "30 yd", w: 8,  h: 22 },
    { label: "40 yd", w: 8,  h: 22 },
  ],
  "field-trailer": [
    { label: "8' × 20'",  w: 8,  h: 20 },
    { label: "10' × 40'", w: 10, h: 40 },
    { label: "12' × 44'", w: 12, h: 44 },
    { label: "12' × 60'", w: 12, h: 60 },
  ],
  "office-trailer": [
    { label: "10' × 40'", w: 10, h: 40 },
    { label: "12' × 60'", w: 12, h: 60 },
    { label: "24' × 60'", w: 24, h: 60 },
  ],
  "temp-office": [
    { label: "8' × 20'",  w: 8,  h: 20 },
    { label: "10' × 40'", w: 10, h: 40 },
    { label: "12' × 60'", w: 12, h: 60 },
  ],
  "porta-potty": [
    { label: "Standard", w: 4, h: 4 },
    { label: "ADA",      w: 5, h: 5 },
    { label: "Bank of 3", w: 12, h: 4 },
  ],
  "crane-tower": [
    { label: "Base 20' × 20'", w: 20, h: 20 },
    { label: "Base 25' × 25'", w: 25, h: 25 },
    { label: "Base 30' × 30'", w: 30, h: 30 },
  ],
  "crane-mobile": [
    { label: "Carrier",           w: 14, h: 50 },
    { label: "Outriggers spread", w: 30, h: 30 },
  ],
  "parking": [
    { label: "1 stall",   w: 9,  h: 18 },
    { label: "2 stalls",  w: 18, h: 18 },
    { label: "5 stalls",  w: 45, h: 18 },
    { label: "10 stalls", w: 90, h: 18 },
  ],
  "fuel-tank": [
    { label: "500 gal",  w: 4, h: 8 },
    { label: "1000 gal", w: 5, h: 12 },
    { label: "2000 gal", w: 8, h: 16 },
  ],
};

/** A footprint that will actually draw: finite, positive, not absurd. */
export function clampFootprint(f: Partial<FootprintFt> | null | undefined): FootprintFt {
  const one = (v: unknown, fallback: number) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(MAX_FT, Math.max(MIN_FT, n));
  };
  return { w: one(f?.w, MIN_FT), h: one(f?.h, MIN_FT) };
}

/** An item's footprint: what was set for it, or what its type usually is. */
export function footprintFt(icon: Pick<SiteIcon, "type"> & { sizeFt?: Partial<FootprintFt> | null }): FootprintFt {
  const fallback = DEFAULT_FOOTPRINT_FT[icon.type] || { w: 10, h: 10 };
  if (!icon.sizeFt) return { ...fallback };
  // A half-set size — one dimension typed, the other not yet — keeps the
  // default for the dimension that is missing instead of collapsing to 1'.
  return clampFootprint({
    w: Number.isFinite(Number(icon.sizeFt.w)) ? icon.sizeFt.w : fallback.w,
    h: Number.isFinite(Number(icon.sizeFt.h)) ? icon.sizeFt.h : fallback.h,
  });
}

/**
 * The footprint in drawing units, or null when the plan has no scale.
 *
 * Null rather than a guess: without a scale there is no honest answer, and an
 * invented one would put a trailer on the drawing at a size that means
 * nothing. The caller falls back to the symbol badge and says why.
 */
export function footprintPx(ft: FootprintFt, feetPerPx: number | null | undefined): { w: number; h: number } | null {
  if (!feetPerPx || !Number.isFinite(feetPerPx) || feetPerPx <= 0) return null;
  return { w: ft.w / feetPerPx, h: ft.h / feetPerPx };
}

// ── Resizing ─────────────────────────────────────────────────────────────────

export type Handle = "nw" | "ne" | "sw" | "se";
export const HANDLES: Handle[] = ["nw", "ne", "sw", "se"];

export interface Rect { x: number; y: number; w: number; h: number }

/** The rect an item occupies, from its centre and its size. */
export function rectAround(cx: number, cy: number, size: { w: number; h: number }): Rect {
  return { x: cx - size.w / 2, y: cy - size.h / 2, w: size.w, h: size.h };
}

export function centreOf(r: Rect): { x: number; y: number } {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

/**
 * A rect redrawn by dragging one corner, with the opposite corner pinned.
 *
 * Pinned rather than growing about the centre: when you drag the top-left of a
 * trailer you are saying where its top-left is, and having the bottom-right
 * slide away from you at the same time is the thing that makes resize handles
 * feel broken.
 *
 * Dragging past the far corner flips rather than refusing, so a corner grabbed
 * by mistake can be dragged back through without the rect sticking at nothing.
 */
export function resizeRect(
  rect: Rect,
  handle: Handle,
  pointer: { x: number; y: number },
  min = 0,
): Rect {
  const left = rect.x, right = rect.x + rect.w;
  const top = rect.y, bottom = rect.y + rect.h;

  const anchorX = handle === "nw" || handle === "sw" ? right : left;
  const anchorY = handle === "nw" || handle === "ne" ? bottom : top;

  let x0 = Math.min(anchorX, pointer.x), x1 = Math.max(anchorX, pointer.x);
  let y0 = Math.min(anchorY, pointer.y), y1 = Math.max(anchorY, pointer.y);

  // Hold the minimum away from the pinned corner, so a rect squashed to
  // nothing reopens on the side being dragged rather than jumping.
  if (x1 - x0 < min) {
    if (anchorX <= pointer.x) x1 = x0 + min; else x0 = x1 - min;
  }
  if (y1 - y0 < min) {
    if (anchorY <= pointer.y) y1 = y0 + min; else y0 = y1 - min;
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** Where a handle sits on a rect. */
export function handlePoint(rect: Rect, handle: Handle): { x: number; y: number } {
  return {
    x: handle === "nw" || handle === "sw" ? rect.x : rect.x + rect.w,
    y: handle === "nw" || handle === "ne" ? rect.y : rect.y + rect.h,
  };
}

/** The cursor a corner handle should show. */
export function handleCursor(handle: Handle): string {
  return handle === "nw" || handle === "se" ? "nwse-resize" : "nesw-resize";
}

// ── Reading them back ────────────────────────────────────────────────────────

/** Feet as a builder writes them: 45', 10'-6", 8" for less than a foot. */
export function formatFt(ft: number): string {
  if (!Number.isFinite(ft)) return "—";
  const total = Math.abs(ft);
  const whole = Math.floor(total + 1e-9);
  const inches = Math.round((total - whole) * 12);
  if (whole === 0) return `${inches || 0}"`;
  if (inches === 0 || inches === 12) return `${whole + (inches === 12 ? 1 : 0)}'`;
  return `${whole}'-${inches}"`;
}

/** "10' × 45'", for a label or a tooltip. */
export function describeFootprint(ft: FootprintFt): string {
  return `${formatFt(ft.w)} × ${formatFt(ft.h)}`;
}

/** Square feet covered, for the properties panel. */
export function footprintArea(ft: FootprintFt): number {
  return ft.w * ft.h;
}

/** Whether a preset is the size an item is currently set to. */
export function matchesPreset(ft: FootprintFt, preset: SizePreset): boolean {
  const near = (a: number, b: number) => Math.abs(a - b) < 0.05;
  return near(ft.w, preset.w) && near(ft.h, preset.h);
}
